import { useEffect, useRef, useState } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { Quote, Star, Check, MapPin } from "lucide-react";
import api from "@/lib/api";

const easeOut = [0.16, 1, 0.3, 1];

function Reveal({ children, delay = 0, className = "" }) {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-15% 0px" });
    return (
        <motion.div
            ref={ref}
            initial={{ opacity: 0, y: 24 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.9, delay, ease: easeOut }}
            className={className}
        >
            {children}
        </motion.div>
    );
}

function StarRow({ rating, setRating, readOnly = false }) {
    return (
        <div className="flex items-center gap-1" role={readOnly ? undefined : "radiogroup"} aria-label="Rating">
            {[1, 2, 3, 4, 5].map((n) => (
                <button
                    key={n}
                    type="button"
                    disabled={readOnly}
                    onClick={() => !readOnly && setRating?.(n)}
                    className={`p-0.5 ${readOnly ? "cursor-default" : "cursor-pointer"} transition-transform ${!readOnly && "hover:scale-110"}`}
                    aria-label={`${n} star${n === 1 ? "" : "s"}`}
                    data-testid={readOnly ? undefined : `star-${n}`}
                >
                    <Star
                        size={readOnly ? 14 : 22}
                        className={n <= rating ? "text-[var(--sb-brand)]" : "text-[var(--sb-border)]"}
                        fill={n <= rating ? "currentColor" : "none"}
                    />
                </button>
            ))}
        </div>
    );
}

function TestimonialCard({ t, i }) {
    return (
        <Reveal delay={0.05 + (i % 6) * 0.05}>
            <article className="relative border border-[var(--sb-border)] bg-[var(--sb-surface)] p-8 h-full group hover:border-[var(--sb-brand)] transition-colors duration-500" data-testid={`testimonial-${t.id}`}>
                <Quote size={24} className="text-[var(--sb-brand)] opacity-40 mb-4" />
                <p className="font-serif text-xl lg:text-2xl leading-[1.4] text-[var(--sb-text)] mb-6">
                    &ldquo;{t.message}&rdquo;
                </p>
                <div className="border-t border-[var(--sb-border)] pt-4 flex items-end justify-between gap-4">
                    <div>
                        <div className="text-sm font-medium text-[var(--sb-text)]">{t.name}</div>
                        {t.location && (
                            <div className="text-xs text-[var(--sb-text-muted)] mt-1 flex items-center gap-1">
                                <MapPin size={10} /> {t.location}
                            </div>
                        )}
                    </div>
                    <StarRow rating={t.rating || 5} readOnly />
                </div>
            </article>
        </Reveal>
    );
}

function LeaveMessageForm({ sourcePage = "", onSuccess }) {
    const [form, setForm] = useState({ name: "", location: "", message: "", rating: 5, email: "" });
    const [state, setState] = useState("idle");
    const [error, setError] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        if (form.name.trim().length < 2 || form.message.trim().length < 4) {
            setError("Please add your name and a message.");
            setState("error");
            return;
        }
        setState("loading");
        setError("");
        try {
            await api.post("/testimonials", { ...form, source_page: sourcePage });
            setState("success");
            onSuccess?.();
        } catch (err) {
            setError(err.response?.data?.detail || "Something went wrong.");
            setState("error");
        }
    };

    if (state === "success") {
        return (
            <div className="border border-[var(--sb-brand)] bg-[var(--sb-surface)] p-8 text-center" data-testid="testimonial-success">
                <Check size={26} className="text-[var(--sb-brand)] mx-auto mb-3" />
                <div className="font-serif text-2xl mb-2">Thank you — from four hundred feet.</div>
                <p className="text-[var(--sb-text-dim)]">
                    Your message is in the queue for moderation. It&rsquo;ll appear on the wall shortly.
                </p>
            </div>
        );
    }

    return (
        <form onSubmit={submit} className="border border-[var(--sb-border)] bg-[var(--sb-surface)] p-8" data-testid="testimonial-form">
            <div className="overline mb-4">Leave a message</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="text-[11px] tracking-[0.28em] uppercase text-[var(--sb-text-muted)] block mb-2">Your name</label>
                    <input
                        type="text"
                        required
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className="input-line w-full"
                        placeholder="Jamie MacLeod"
                        data-testid="testimonial-name"
                    />
                </div>
                <div>
                    <label className="text-[11px] tracking-[0.28em] uppercase text-[var(--sb-text-muted)] block mb-2">Where from (optional)</label>
                    <input
                        type="text"
                        value={form.location}
                        onChange={(e) => setForm({ ...form, location: e.target.value })}
                        className="input-line w-full"
                        placeholder="Inverness, Scotland"
                        data-testid="testimonial-location"
                    />
                </div>
                <div className="md:col-span-2">
                    <label className="text-[11px] tracking-[0.28em] uppercase text-[var(--sb-text-muted)] block mb-2">Your message</label>
                    <textarea
                        required
                        rows={4}
                        value={form.message}
                        onChange={(e) => setForm({ ...form, message: e.target.value })}
                        className="input-line w-full resize-none"
                        placeholder="What you loved, what moved you, or a quick word of support…"
                        data-testid="testimonial-message"
                    />
                </div>
                <div className="md:col-span-2 flex flex-wrap items-center justify-between gap-4 pt-2">
                    <div className="flex items-center gap-4">
                        <span className="text-[11px] tracking-[0.28em] uppercase text-[var(--sb-text-muted)]">Rating</span>
                        <StarRow rating={form.rating} setRating={(r) => setForm({ ...form, rating: r })} />
                    </div>
                    <button
                        type="submit"
                        disabled={state === "loading"}
                        className="btn-primary"
                        data-testid="testimonial-submit"
                    >
                        {state === "loading" ? "Sending…" : "Send message"}
                    </button>
                </div>
            </div>
            {state === "error" && error && (
                <p className="mt-4 text-sm text-red-300" data-testid="testimonial-error">{error}</p>
            )}
            <p className="mt-6 text-xs text-[var(--sb-text-muted)]">
                All messages are reviewed before appearing on the wall. No email or personal data is displayed publicly.
            </p>
        </form>
    );
}

export default function TestimonialsWall({ sourcePage = "", title, subtitle, limit = 6 }) {
    const [items, setItems] = useState([]);
    const [showForm, setShowForm] = useState(false);

    const load = () => {
        api.get("/testimonials", { params: { source_page: sourcePage || undefined, limit } })
            .then((r) => setItems(r.data))
            .catch(() => setItems([]));
    };

    useEffect(() => { load(); }, [sourcePage]);

    return (
        <section
            className="max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32 border-t border-[var(--sb-border)]"
            data-testid="testimonials-wall"
        >
            <Reveal>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-14 items-end">
                    <div className="lg:col-span-7">
                        <div className="overline mb-4">{items.length > 0 ? `The wall · ${items.length}` : "The wall"}</div>
                        <h2 className="font-serif tracking-tight leading-[0.98]" style={{ fontSize: "clamp(2.2rem, 5vw, 4rem)" }}>
                            {title || (
                                <>Words from the ground.<br /><span className="italic text-[var(--sb-brand)]">Support from the sky.</span></>
                            )}
                        </h2>
                    </div>
                    <div className="lg:col-span-5 text-[var(--sb-text-dim)] text-lg leading-relaxed">
                        {subtitle || "Reactions, testimonials, and quiet words of support from visitors who&rsquo;ve been up in the wind with me."}
                    </div>
                </div>
            </Reveal>

            {items.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="testimonials-grid">
                    {items.map((t, i) => <TestimonialCard key={t.id} t={t} i={i} />)}
                </div>
            ) : (
                <Reveal>
                    <div className="border border-dashed border-[var(--sb-border)] p-10 lg:p-14 text-center text-[var(--sb-text-dim)]">
                        <Quote size={26} className="text-[var(--sb-brand)] opacity-50 mx-auto mb-4" />
                        <p className="font-serif text-xl">Be the first to leave a message on the wall.</p>
                    </div>
                </Reveal>
            )}

            <Reveal className="mt-14" delay={0.15}>
                <AnimatePresence mode="wait">
                    {showForm ? (
                        <motion.div
                            key="form"
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -12 }}
                            transition={{ duration: 0.5, ease: easeOut }}
                        >
                            <LeaveMessageForm sourcePage={sourcePage} onSuccess={load} />
                        </motion.div>
                    ) : (
                        <motion.div
                            key="cta"
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -12 }}
                            transition={{ duration: 0.5, ease: easeOut }}
                            className="text-center"
                        >
                            <button
                                type="button"
                                onClick={() => setShowForm(true)}
                                className="btn-outline"
                                data-testid="testimonials-open-form"
                            >
                                Leave your own message
                            </button>
                        </motion.div>
                    )}
                </AnimatePresence>
            </Reveal>
        </section>
    );
}
